import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, ListChecks, ChevronRight } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useChecklists } from '../hooks/useChecklists';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { FeatureDisabled } from '@/components/FeatureDisabled';
import { useTranslation } from '@parentpath/i18n';
import type { ChecklistWithProgress, ChecklistTemplate } from '../types';

// Inline ChecklistCard component
function ChecklistCard({ checklist }: { checklist: ChecklistWithProgress }) {
  const { progress } = checklist;

  return (
    <a href={`/tools/checklists/${checklist.id}`} className="checklist-card">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white font-medium">{checklist.title}</span>
          <span className="text-white/60 text-sm">{progress.completed} / {progress.total}</span>
        </div>
        <div className="checklist-progress-bar">
          <div className="checklist-progress-fill" style={{ width: `${progress.percentage}%` }}></div>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-pp-orange" />
    </a>
  );
}

// Inline TemplateCard component
function TemplateCard({
  template,
  onAdd,
  isLoading,
}: {
  template: ChecklistTemplate;
  onAdd: () => void;
  isLoading?: boolean;
}) {
  const { t } = useTranslation('checklists');

  return (
    <div className="template-card">
      <h3 className="text-white font-semibold text-base mb-1">{template.title}</h3>
      {template.description && (
        <p className="text-white/80 text-sm mb-3">{template.description}</p>
      )}
      <div className="flex items-center justify-between">
        <span className="text-pp-orange text-sm">{t('templates.entries', { count: template.item_count })}</span>
        <button
          onClick={onAdd}
          disabled={isLoading}
          className="add-template-btn"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Plus className="w-4 h-4" />
              {t('templates.add')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export function ChecklistsPage() {
  const navigate = useNavigate();
  const isChecklistsEnabled = useFeatureEnabled('feature_checklists');
  const { t } = useTranslation('checklists');
  const {
    checklists,
    templates,
    isLoading,
    addTemplateChecklist,
    createCustomChecklist,
  } = useChecklists();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [addingTemplateId, setAddingTemplateId] = useState<string | null>(null);

  // Show disabled message if feature is turned off
  if (!isChecklistsEnabled) {
    return (
      <FeatureDisabled
        featureName={t('featureDisabled.name')}
        description={t('featureDisabled.description')}
      />
    );
  }

  // Check which templates are already added
  const addedTemplateIds = new Set(
    checklists.filter(c => c.template_id).map(c => c.template_id!)
  );

  // Available templates (not yet added)
  const availableTemplates = templates.filter(t => !addedTemplateIds.has(t.id));

  const handleAddTemplate = async (templateId: string) => {
    setAddingTemplateId(templateId);
    const newChecklist = await addTemplateChecklist(templateId);
    setAddingTemplateId(null);
    if (newChecklist) {
      navigate(`/tools/checklists/${newChecklist.id}`);
    }
  };

  const handleCreateCustom = async () => {
    if (!newListTitle.trim()) return;
    setIsCreating(true);
    const newChecklist = await createCustomChecklist(newListTitle.trim());
    setNewListTitle('');
    setShowCreateModal(false);
    setIsCreating(false);
    if (newChecklist) {
      navigate(`/tools/checklists/${newChecklist.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-4">{t('pageTitle')}</h1>

      {/* Active Checklists Section */}
      {checklists.length > 0 && (
        <section className="space-y-3 mb-6">
          {checklists.map((checklist) => (
            <ChecklistCard key={checklist.id} checklist={checklist} />
          ))}
        </section>
      )}

      {/* Empty State */}
      {checklists.length === 0 && (
        <section className="mb-6">
          <div className="text-center py-8">
            <div className="empty-icon">
              <ListChecks className="w-7 h-7 text-white/50" />
            </div>
            <h2 className="text-white font-medium text-lg mb-1">{t('empty.title')}</h2>
            <p className="text-pp-orange text-sm">
              {t('empty.description')}
            </p>
          </div>
        </section>
      )}

      {/* Create List Button */}
      <div className="mt-6">
        {!showCreateModal ? (
          <button className="create-btn" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-5 h-5" />
            {t('create.button')}
          </button>
        ) : (
          <div className="glass-effect rounded-2xl p-4 space-y-3">
            <Input
              placeholder={t('create.placeholder')}
              value={newListTitle}
              onChange={(e) => setNewListTitle(e.target.value)}
              autoFocus
              className="bg-white/10 border-white/25 text-white placeholder:text-white/50"
            />
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="flex-1 bg-white/20 text-white border-white/25"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewListTitle('');
                }}
              >
                {t('create.cancel')}
              </Button>
              <Button
                className="flex-1 bg-pp-orange hover:bg-pp-orange/90"
                onClick={handleCreateCustom}
                disabled={!newListTitle.trim() || isCreating}
              >
                {isCreating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('create.submit')
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Vorlagen Section */}
      {availableTemplates.length > 0 && (
        <section className="mt-8">
          <h2 className="checklist-section-title">{t('templates.title')}</h2>
          <div className="space-y-3">
            {availableTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onAdd={() => handleAddTemplate(template.id)}
                isLoading={addingTemplateId === template.id}
              />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
